import { get } from 'object-path-immutable';
import {
  applyThemeInLocalizedBlock,
  calculateOverridePathsInLocalizedBlock,
  calculateOverridePaths,
  labelForOverrideKey,
  styleToThemeStyle,
  augmentedWrap,
} from './utils';

const PATHS = {
  root: 'style',
  spans: 'spans',
};

const themeAdder = (content, selectedVariant, preserveOverrides) =>
  augmentedWrap(content)
    .deleteProperties([PATHS.spans], preserveOverrides)
    .updateStyle(PATHS.root, { theme: selectedVariant }, preserveOverrides)
    .value();

const augmentOverridesWithSpanPaths = ({
  content,
  path,
  pathName,
  regularOverridePaths,
}) => {
  const textPrimitive = get(content, path);
  const keyPrefix = pathName ? `${pathName}.` : '';
  const pathPrefix = path ? `${path}.` : '';

  // Map style keys to span paths
  const spanStyleKeyMap = (textPrimitive.spans ?? []).reduce(
    (keyMap, span, index) =>
      Object.keys(span.style ?? {}).reduce(
        (acc, key) => ({
          ...acc,
          [key]: [
            ...(acc[key] ?? []),
            `${pathPrefix}spans.${index}.style.${key}`,
          ],
        }),
        keyMap
      ),
    {}
  );

  const overridePathsWithSpanPaths = Object.entries(spanStyleKeyMap).map(
    ([key, spanPaths]) => {
      const existingOverride = regularOverridePaths.find(
        override => override.key === key
      );
      if (existingOverride) {
        // Update existing override paths to include span paths
        return {
          ...existingOverride,
          paths: [...existingOverride.paths, ...spanPaths],
        };
      }

      // Add new override for span style key
      return {
        key: `${keyPrefix}${key}`,
        label: labelForOverrideKey(`${keyPrefix}${key}`),
        paths: spanPaths,
      };
    }
  );

  // Merge span overrides with regular overrides, replacing any existing ones
  return [
    ...regularOverridePaths.filter(
      override =>
        !overridePathsWithSpanPaths.some(
          spanOverride => spanOverride.key === override.key
        )
    ),
    ...overridePathsWithSpanPaths,
  ];
};

const overridesCalculator = ({ compiledTheme, content, path, pathName }) => {
  const regularOverridePaths = calculateOverridePaths({
    compiledTheme,
    content,
    path: path ? `${path}.style` : PATHS.root,
    pathName,
    ignore: ['horizontalAlignment'],
    group: { textAlignment: ['horizontalAlignment'] },
  });

  return augmentOverridesWithSpanPaths({
    content,
    path,
    pathName,
    regularOverridePaths,
  });
};

export const addTheme = (content, selectedVariant, preserveOverrides) => {
  return applyThemeInLocalizedBlock(
    content,
    selectedVariant,
    preserveOverrides,
    themeAdder
  );
};

export const overrides = args =>
  calculateOverridePathsInLocalizedBlock(args, overridesCalculator);

export const extractStyles = content => ({
  $style: styleToThemeStyle({
    style: get(content, PATHS.root),
  }),
});
